import { Job, SupabaseJob, formatDateForDb, formatDateTimeForDb, getToday, addDays } from '@applicator/shared'

import { supabase } from '../../lib/supabase'
import { DateMode } from '../JobDateChips/types'

import { DateField } from './columns/types'
import { UserRules } from './types'
import { mapSupabaseToJob, convertDateToSupabaseFormat, computeEffectiveDate, formatDateTime, formatDate } from './utils'

interface DateHandlersParams {
  jobs: Job[]
  supabaseJobs: SupabaseJob[]
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
  userRules: UserRules
}

export const createDateHandlers = ({
  jobs,
  supabaseJobs,
  setJobs,
  setSupabaseJobs,
  setError,
  userRules,
}: DateHandlersParams) => {
  const handleDateChange = async (rowIndex: number, field: DateField, dateValue: string) => {
    const job = jobs[rowIndex]
    if (!job.id) {
      // Fallback for jobs without id
      const updatedJobs = [...jobs]
      updatedJobs[rowIndex] = { ...updatedJobs[rowIndex], [field]: dateValue }
      setJobs(updatedJobs)
      return
    }

    // Определяем поле в Supabase и его тип (нужно сделать до преобразования даты)
    let isTimestamp: boolean = false
    switch (field as 'deadline' | 'dateApplied' | 'followUp' | 'dateSaved') {
      case 'deadline':
        isTimestamp = true // TIMESTAMPTZ
        break
      case 'dateApplied':
        isTimestamp = true // TIMESTAMPTZ
        break
      case 'followUp':
        // DATE field
        break
      case 'dateSaved':
        // Date Saved is read-only, should not be updated
        return
      default:
        return
    }

    // Преобразуем дату в нужный формат (нужно сделать до optimistic update)
    const supabaseDate = convertDateToSupabaseFormat(dateValue, isTimestamp)
    
    if (!supabaseDate) {
      // Invalid date format, don't update
      return
    }

    // Optimistic update - update both supabaseJobs and jobs
    // First, update supabaseJobs with mode and manual fields
    setSupabaseJobs((prev) => {
      return prev.map(j => {
        if (j.id === job.id) {
          const updated = { ...j }
          // Update mode and manual fields
          switch (field) {
            case 'deadline':
              updated.deadline_mode = 'manual'
              updated.deadline_manual = supabaseDate
              updated.deadline_at = supabaseDate
              break
            case 'dateApplied':
              updated.date_applied_mode = 'manual'
              updated.date_applied_manual = supabaseDate
              updated.applied_at = supabaseDate
              break
            case 'followUp':
              updated.follow_up_mode = 'manual'
              updated.follow_up_manual = supabaseDate
              updated.follow_up_at = supabaseDate
              break
          }
          return updated
        }
        return j
      })
    })
    
    // Then update jobs - only update the changed field to prevent unnecessary re-renders
    // This prevents other cells (company, dateSaved, etc.) from flashing
    setJobs((prev) => {
      return prev.map(j => {
        if (j.id === job.id) {
          const updatedJob = { ...j }
          
          // Only update the changed field, keep other fields unchanged
          switch (field) {
            case 'deadline':
              updatedJob.deadline = dateValue || 'N/A'
              break
            case 'dateApplied':
              updatedJob.dateApplied = dateValue || 'N/A'
              break
            case 'followUp':
              updatedJob.followUp = dateValue || 'Add date'
              break
          }
          
          return updatedJob
        }
        return j
      })
    })

    // Определяем поле в Supabase и его тип
    let supabaseField: string
    switch (field) {
      case 'deadline':
        supabaseField = 'deadline_at'
        break
      case 'dateApplied':
        supabaseField = 'applied_at'
        break
      case 'followUp':
        supabaseField = 'follow_up_at'
        break
      case 'dateSaved':
        supabaseField = 'saved_at'
        break
      default:
        return
    }

    // Определяем mode и manual поля для обновления
    // supabaseDate уже вычислен выше в optimistic update
    let modeColumn: string
    let manualColumn: string
    switch (field as 'deadline' | 'dateApplied' | 'followUp' | 'dateSaved') {
      case 'deadline':
        modeColumn = 'deadline_mode'
        manualColumn = 'deadline_manual'
        break
      case 'dateApplied':
        modeColumn = 'date_applied_mode'
        manualColumn = 'date_applied_manual'
        break
      case 'followUp':
        modeColumn = 'follow_up_mode'
        manualColumn = 'follow_up_manual'
        break
      case 'dateSaved':
        // dateSaved is read-only, should not be updated manually
        return
      default:
        return
    }

    // Update in Supabase - set mode to 'manual' and update both main field and manual field
    const updateData: Record<string, unknown> = {
      [supabaseField]: supabaseDate,
      [modeColumn]: 'manual',
      [manualColumn]: supabaseDate,
    }

    const { error, data } = await supabase
      .from('jobs')
      .update(updateData)
      .eq('id', job.id)
      .select('id,applied_at,deadline_at,follow_up_at,date_applied_mode,date_applied_manual,deadline_mode,deadline_manual,follow_up_mode,follow_up_manual')
      .single()

    if (error) {
      // Revert on error
      setJobs(jobs)
      setError(error.message)
    } else if (data) {
      const updatedSupabaseJob = data as SupabaseJob
      
      // Update supabaseJobs with the updated data (including mode and manual fields)
      setSupabaseJobs((prev) => {
        const updated = prev.map(j => 
          j.id === job.id 
            ? { ...j, ...updatedSupabaseJob } 
            : j
        )
        // If job not in list, add it
        if (!prev.find(j => j.id === job.id)) {
          return [...prev, updatedSupabaseJob]
        }
        return updated
      })
      
      // Update jobs state - only update the changed field to prevent unnecessary re-renders
      // This prevents other cells (company, dateSaved, etc.) from flashing
      setJobs((prev) => {
        const updated = [...prev]
        const jobIndex = updated.findIndex(j => j.id === job.id)
        if (jobIndex >= 0) {
          const currentJob = updated[jobIndex]
          // Only recalculate the changed field, keep other fields unchanged
          const updatedJob = { ...currentJob }
          
          // Recalculate only the changed field
          switch (field) {
            case 'deadline': {
              const effectiveDeadline = computeEffectiveDate(
                updatedSupabaseJob.deadline_mode,
                updatedSupabaseJob.deadline_manual,
                updatedSupabaseJob.deadline_at,
                'deadline',
                updatedSupabaseJob,
                userRules
              )
              updatedJob.deadline = effectiveDeadline ? formatDateTime(effectiveDeadline) : 'N/A'
              break
            }
            case 'dateApplied': {
              const effectiveDateApplied = computeEffectiveDate(
                updatedSupabaseJob.date_applied_mode,
                updatedSupabaseJob.date_applied_manual,
                updatedSupabaseJob.applied_at,
                'date_applied',
                updatedSupabaseJob,
                userRules
              )
              updatedJob.dateApplied = effectiveDateApplied ? formatDateTime(effectiveDateApplied) : 'N/A'
              break
            }
            case 'followUp': {
              const effectiveFollowUp = computeEffectiveDate(
                updatedSupabaseJob.follow_up_mode,
                updatedSupabaseJob.follow_up_manual,
                updatedSupabaseJob.follow_up_at,
                'follow_up',
                updatedSupabaseJob,
                userRules
              )
              updatedJob.followUp = effectiveFollowUp ? formatDate(effectiveFollowUp) : 'Add date'
              break
            }
          }
          
          updated[jobIndex] = updatedJob
        }
        return updated
      })
    }
  }

  const handleQuickAction = async (rowIndex: number, field: 'deadline' | 'dateApplied' | 'followUp', action: 'today' | '+3d' | '+7d' | 'clear' | 'revert') => {
    const job = jobs[rowIndex]
    if (!job.id) return

    const today = getToday()
    let date: Date | null = null
    let mode: DateMode = 'manual'
    const isTimestamp = field === 'dateApplied' || field === 'deadline'

    switch (action) {
      case 'today':
        // For TIMESTAMPTZ fields, use current time; for DATE fields, use midnight
        if (isTimestamp) {
          date = new Date() // Current date and time
        } else {
          date = today // Midnight
        }
        break
      case '+3d':
        date = addDays(today, 3)
        break
      case '+7d':
        date = addDays(today, 7)
        break
      case 'clear':
        mode = 'cleared'
        break
      case 'revert':
        mode = 'auto'
        break
    }

    // Format date for database
    const dbValue = isTimestamp
      ? formatDateTimeForDb(date)
      : formatDateForDb(date)

    // Determine field mappings
    let supabaseField: string
    let modeColumn: string
    let manualColumn: string
    switch (field) {
      case 'deadline':
        supabaseField = 'deadline_at'
        modeColumn = 'deadline_mode'
        manualColumn = 'deadline_manual'
        break
      case 'dateApplied':
        supabaseField = 'applied_at'
        modeColumn = 'date_applied_mode'
        manualColumn = 'date_applied_manual'
        break
      case 'followUp':
        supabaseField = 'follow_up_at'
        modeColumn = 'follow_up_mode'
        manualColumn = 'follow_up_manual'
        break
      default:
        return
    }

    // Prepare update data
    const updateData: Record<string, unknown> = {
      [modeColumn]: mode,
      [manualColumn]: dbValue,
    }

    // If mode is 'manual' and we have a value, also update the main field
    if (mode === 'manual' && dbValue) {
      updateData[supabaseField] = dbValue
    } else if (mode === 'cleared' || mode === 'auto') {
      // For 'cleared' or 'auto', set main field to null (will be computed for auto)
      updateData[supabaseField] = null
    }

    // Update in Supabase
    const { error, data } = await supabase
      .from('jobs')
      .update(updateData)
      .eq('id', job.id)
      .select('id,applied_at,deadline_at,follow_up_at,date_applied_mode,date_applied_manual,deadline_mode,deadline_manual,follow_up_mode,follow_up_manual')
      .single()

    if (error) {
      setError(error.message)
    } else if (data) {
      const updatedSupabaseJob = data as SupabaseJob
      
      // Update supabaseJobs with the updated data
      setSupabaseJobs((prev) => {
        const updated = prev.map(j => 
          j.id === job.id 
            ? { ...j, ...updatedSupabaseJob } 
            : j
        )
        if (!prev.find(j => j.id === job.id)) {
          return [...prev, updatedSupabaseJob]
        }
        return updated
      })
      
      // Update jobs state with formatted dates
      const mappedJob = mapSupabaseToJob(updatedSupabaseJob, userRules)
      setJobs((prev) => {
        const updated = [...prev]
        const jobIndex = updated.findIndex(j => j.id === job.id)
        if (jobIndex >= 0) {
          updated[jobIndex] = { ...updated[jobIndex], ...mappedJob }
        }
        return updated
      })
    }
  }

  return {
    handleDateChange,
    handleQuickAction,
  }
}
